/**
 * ----------------------------------------------------------------------------------------------------------------------
 * VARIANT PICKER SYNC
 * ----------------------------------------------------------------------------------------------------------------------
 *
 * Keeps the variant that is added to the cart in sync with the size the customer sees selected. Loaded once for every
 * page in layout/theme.liquid, BEFORE "default-size.js".
 *
 * The theme's <variant-picker> (theme.js, Stretch) only writes the chosen variant to the form's hidden "id" input
 * when an option input sends a "change" event, and only once the re-rendered section for that combination has been
 * fetched. Two gaps follow from it:
 *
 * 1. Options picked before theme.js has run. The option inputs are plain radios, so a click while the page is still
 *    loading checks the size, but nothing listens yet. Once the picker connects, it keeps the variant rendered by
 *    Liquid (the first available one), while the clicked size still shows as selected — and clicking it again sends
 *    no "change" event, as it is already checked. Add to cart then adds the first available size.
 *    Fix: when a picker connects, an option input that is checked but was not checked in the rendered markup was
 *    picked by the customer, and a "change" event is sent for it, as if it had been picked now.
 *
 * 2. Add to cart pressed while a variant change is still loading. The hidden "id" input still holds the previous
 *    variant until the fetch resolves.
 *    Fix: a submit of the picker's form is held back until the pending change has been applied, then sent again.
 *
 * 3. Add to cart pressed before theme.js has run. No <product-form> listens yet, so the browser posts the form itself,
 *    with the variant rendered by Liquid, and redirects to the cart page.
 *    Fix: snippets/product-form-gate.liquid (inline in the head) holds that submit back ("window.productFormGate"). It is sent again
 *    here once every deferred script has run (so that the picks of 1. and the default size are applied first), and
 *    then waits for those changes as in 2.
 *
 * Neither changes theme.js, so the theme can still be updated: if a later Stretch version fixes this, remove this file.
 */

(() => {
  const handledPickers = new WeakSet();
  const pendingChanges = new Map(); // form id => promise of the variant change being applied

  const syncEarlyPick = (picker) => {
    if (handledPickers.has(picker)) {
      return;
    }

    handledPickers.add(picker);

    const formId = picker.getAttribute('form-id');

    // "defaultChecked" reflects the "checked" attribute rendered by Liquid. Markup re-rendered by the theme, or cloned
    // into a quick buy panel, always matches it: only a pick made before the picker connected differs.
    const pickedInputs = Array.from(document.querySelectorAll(`input[data-option-position][form="${formId}"]`)).filter(
      (input) => input.checked && !input.defaultChecked
    );

    if (pickedInputs.length === 0) {
      return;
    }

    // The theme resolves the combination from every checked input of the form, so one event is enough. An input linked
    // to another product (combined listing) must be the one sent, as the theme checks it to load that product.
    const input = pickedInputs.find((pickedInput) => pickedInput.hasAttribute('data-product-url')) ?? pickedInputs[0];
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  customElements.whenDefined('variant-picker').then(() => {
    const VariantPicker = customElements.get('variant-picker');
    const { connectedCallback, selectCombination } = VariantPicker.prototype;

    VariantPicker.prototype.connectedCallback = function () {
      const result = connectedCallback.call(this);
      syncEarlyPick(this);

      return result;
    };

    VariantPicker.prototype.selectCombination = function (...args) {
      const formId = this.getAttribute('form-id');
      const promise = selectCombination.apply(this, args);

      pendingChanges.set(formId, promise);

      const clear = () => {
        if (pendingChanges.get(formId) === promise) {
          pendingChanges.delete(formId);
        }
      };

      promise.then(clear, clear);

      return promise;
    };

    // Pickers already on the page were connected before the prototype was extended.
    document.querySelectorAll('variant-picker').forEach(syncEarlyPick);
  });

  // DOMContentLoaded fires once every deferred and module script has run: theme.js, this file and "default-size.js",
  // so every size change has been started (and registered as pending) by then.
  const domContentLoaded = new Promise((resolve) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', resolve, { once: true });
    } else {
      resolve();
    }
  });

  Promise.all([
    customElements.whenDefined('variant-picker'),
    customElements.whenDefined('product-form'),
    domContentLoaded,
  ]).then(() => {
    const gate = window.productFormGate;

    if (!gate || gate.open) {
      return;
    }

    gate.open = true;

    const { queued } = gate;
    gate.queued = null;

    if (!queued?.form.isConnected) {
      return;
    }

    const { form, submitter } = queued;
    form.requestSubmit(submitter?.isConnected && submitter.form === form ? submitter : null);
  });

  // Caught on its way down, before the theme's <product-form> listener on the form itself.
  document.addEventListener(
    'submit',
    (event) => {
      const formId = event.target.getAttribute('id'); // Not "form.id": the form's "id" input shadows that property.
      const pendingChange = formId && pendingChanges.get(formId);

      if (!pendingChange) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const { submitter } = event;

      // A quick buy panel replaces its whole form on a variant change, so the current form is looked up again. If
      // another change was started meanwhile, this submit is held back again until that one is applied too.
      const resubmit = () => {
        const form = document.getElementById(formId);

        if (!form) {
          return;
        }

        form.requestSubmit(submitter?.isConnected && submitter.form === form ? submitter : null);
      };

      pendingChange.then(resubmit, resubmit);
    },
    { capture: true }
  );
})();
